import { z } from 'zod';

import { apiErrorResponseSchema, dateSchema, datetimeSchema, EmailStringSchema } from '../../common';
import { cursorPaginateQuerySchema, cursorPaginateResponseSchema } from '../common';
import { IntegrationName } from '../integration/integration';

/**
 * Launch Plan contract — `@kouto/types` half of the *Way Launch Plan API (Internal · Preview)*.
 *
 * `IntegrationName` is reused from `../integration/integration` (the existing 12-value enum already
 * matches the contract exactly), not redefined here.
 *
 * Enum wire values are pinned to the contract; the TS symbol names are prefixed `LaunchPlan*` to stay
 * unambiguous in the flat barrel export (`Stage` / `StepKey` are too generic to re-export globally).
 */

export enum LaunchPlanStage {
  KICKOFF = 'kickoff',
  LAUNCH = 'launch',
  RECOMMENDED = 'recommended',
}

export enum LaunchPlanStepKey {
  PROPERTY_PROFILE = 'propertyProfile',
  CONFIRM_INTEGRATIONS = 'confirmIntegrations',
  INVITE_FINANCE_CONTACT = 'inviteFinanceContact',
  SUBMIT_EXPERIENCE_CONTENT = 'submitExperienceContent',
  SETUP_PAYMENTS = 'setupPayments',
  SETUP_WEBSITE = 'setupWebsite',
  CONFIRM_LAUNCH_DATE = 'confirmLaunchDate',
  INVITE_TEAM = 'inviteTeam',
  SETUP_INTEGRATION = 'setupIntegration',
  SETUP_RECOMMENDED_INTEGRATION = 'setupRecommendedIntegration',
  ADD_EMAIL_DNS_RECORD = 'addEmailDnsRecord',
  OPERATIONAL_AUDIT_EXPERIENCES = 'operationalAuditExperiences',
  MARKETING_AUDIT_EXPERIENCES = 'marketingAuditExperiences',
  SETUP_BRANDING = 'setupBranding',
  LAUNCH_TESTING = 'launchTesting',
  BOOKING_MIGRATION = 'bookingMigration',
  PROVIDE_PRODUCTION_SCRIPT_ENV = 'provideProductionScriptEnv',
  REVIEW_AND_APPROVE = 'reviewAndApprove',
  CUSTOMIZE_EMAILS = 'customizeEmails',
  SETUP_APPLE_PAY = 'setupApplePay',
  PUBLISH_EXPERIENCE = 'publishExperience',
}

/**
 * Stable RFC 9457 `type` URIs for launch-plan error responses. Illustrative, extensible set — the
 * exception filter that emits `Problem` bodies lands in PR4.
 */
export const LAUNCH_PLAN_ERROR_TYPE_BASE = 'https://docs.way.co/errors/launch-plan';

export const LAUNCH_PLAN_ERROR_TYPES = {
  DERIVED_STEP_NOT_MANUAL: `${LAUNCH_PLAN_ERROR_TYPE_BASE}/derived-step-not-manual`,
  DUPLICATE_BRAND: `${LAUNCH_PLAN_ERROR_TYPE_BASE}/duplicate-brand`,
  NOT_FOUND: `${LAUNCH_PLAN_ERROR_TYPE_BASE}/not-found`,
  VALIDATION: `${LAUNCH_PLAN_ERROR_TYPE_BASE}/validation`,
} as const;

/** RFC 9457 `application/problem+json` — the canonical `apiErrorResponseSchema` plus `instance`. */
export const LaunchPlanProblemSchema = apiErrorResponseSchema.extend({
  instance: z.string().optional(),
});

export const LaunchPlanStepOwnerSchema = z.object({
  email: EmailStringSchema,
  name: z.string(),
  userId: z.string().uuid(),
});

/**
 * Integration options offered on a launch plan — a curated subset of `IntegrationName`. The launch plan
 * owns which integrations it offers and does NOT auto-track `IntegrationName`: adding a new
 * `IntegrationName` must be a deliberate addition to this list. KICC is intentionally excluded (still a
 * valid `IntegrationName` elsewhere, e.g. payment gateways). Keep in sync with the API's
 * `LaunchPlanIntegration` enum — ADR-005 forbids that Data-layer enum from importing this contract.
 */
const LAUNCH_PLAN_INTEGRATIONS = [
  IntegrationName.ADOBE_ANALYTICS,
  IntegrationName.ALICE,
  IntegrationName.GA4_ANALYTICS,
  IntegrationName.GTM_ANALYTICS,
  IntegrationName.INFOR,
  IntegrationName.MEWS,
  IntegrationName.OPERA,
  IntegrationName.REVINATE,
  IntegrationName.SIMPHONY,
  IntegrationName.STAYNTOUCH,
  IntegrationName.TEALIUM_ANALYTICS,
] as const;

const LaunchPlanIntegrationSchema = z.enum(LAUNCH_PLAN_INTEGRATIONS);

/**
 * Per-category "Other" intent markers. Selecting "Other" for a group (the brand uses a system Way does not
 * integrate with) is recorded on the plan's `integrations` array as `<category>:other` to capture intent.
 * These are NOT connectable integrations — no setup step is shown for them and recompute ignores them.
 * ("None" is a frontend-only opt-out and stores nothing.)
 */
export enum LaunchPlanIntegrationIntent {
  CRM_OTHER = 'crm:other',
  PMS_OTHER = 'pms:other',
  POS_OTHER = 'pos:other',
  TRACKING_OTHER = 'tracking:other',
}

/** A value stored in a plan's `integrations` array: a connectable integration or an "Other" intent marker. */
const LaunchPlanIntegrationSelectionSchema = z.union([
  LaunchPlanIntegrationSchema,
  z.nativeEnum(LaunchPlanIntegrationIntent),
]);

export const LaunchPlanSchema = z.object({
  brandId: z.string().uuid(),
  createdAt: datetimeSchema,
  // Tri-state: `null` = integrations not confirmed yet, `[]` = confirmed none, non-empty = selected set.
  integrations: z.array(LaunchPlanIntegrationSelectionSchema).nullable(),
  kickoffDate: dateSchema.nullable(),
  launchPlanId: z.string().uuid(),
  targetLaunchDate: dateSchema.nullable(),
  updatedAt: datetimeSchema,
});

export const LaunchPlanStepSchema = z.object({
  completedAt: datetimeSchema.nullable(),
  createdAt: datetimeSchema,
  dueDate: dateSchema.nullable(),
  launchPlanId: z.string().uuid(),
  launchPlanStepId: z.string().uuid(),
  order: z.number().int(),
  owner: LaunchPlanStepOwnerSchema.nullable(),
  required: z.boolean(),
  stage: z.nativeEnum(LaunchPlanStage),
  stepKey: z.nativeEnum(LaunchPlanStepKey),
  updatedAt: datetimeSchema,
});

export const CreateLaunchPlanRequestSchema = z.object({
  brandId: z.string().uuid(),
  integrations: z.array(LaunchPlanIntegrationSelectionSchema).optional(),
  kickoffDate: dateSchema.nullable().optional(),
  targetLaunchDate: dateSchema.nullable().optional(),
});

/** `application/merge-patch+json` — present keys are set, `null` clears. */
export const LaunchPlanMergePatchSchema = z
  .object({
    integrations: z.array(LaunchPlanIntegrationSelectionSchema),
    kickoffDate: dateSchema.nullable(),
    targetLaunchDate: dateSchema.nullable(),
  })
  .partial();

/**
 * `application/merge-patch+json` — present keys are set, `null` clears.
 * `completed` is honoured for manual steps only (a derived step → 422, enforced in PR4).
 * `ownerId` is the userId of an existing brand member (`null` unassigns); the handler denormalises
 * name/email into the owner columns.
 */
export const LaunchPlanStepMergePatchSchema = z
  .object({
    completed: z.boolean(),
    dueDate: dateSchema.nullable(),
    ownerId: z.string().uuid().nullable(),
  })
  .partial();

export const PaginatedLaunchPlansSchema = cursorPaginateResponseSchema(LaunchPlanSchema);

export const LaunchPlanStepsListSchema = z.object({
  items: z.array(LaunchPlanStepSchema),
});

export const GetLaunchPlansQuerySchema = cursorPaginateQuerySchema.extend({
  brandId: z.preprocess((value) => (typeof value === 'string' ? [value] : value), z.array(z.string().uuid()).optional()),
  organizationId: z.string().uuid().optional(),
});

export const RecomputeLaunchPlansRequestSchema = z.object({
  brandId: z.string().uuid().optional(),
  launchPlanId: z.string().uuid().optional(),
});

/** Frontend-debounced flush: the step ids the user just assigned, for the batched task-assignment email. */
export const NotifyLaunchPlanAssignmentsRequestSchema = z.object({
  stepIds: z.array(z.string().uuid()).min(1),
});

export const RecomputeLaunchPlansResultSchema = z.object({
  checked: z.number().int(),
  newlyCompleted: z.number().int(),
  plansProcessed: z.number().int(),
  recomputedAt: datetimeSchema,
  skipped: z.number().int(),
});

export type LaunchPlanIntegrationName = z.infer<typeof LaunchPlanIntegrationSchema>;
export type LaunchPlanIntegrationSelection = z.infer<typeof LaunchPlanIntegrationSelectionSchema>;
export type LaunchPlanProblem = z.infer<typeof LaunchPlanProblemSchema>;
export type LaunchPlanStepOwner = z.infer<typeof LaunchPlanStepOwnerSchema>;
export type LaunchPlanType = z.infer<typeof LaunchPlanSchema>;
export type LaunchPlanStepType = z.infer<typeof LaunchPlanStepSchema>;
export type CreateLaunchPlanRequestType = z.infer<typeof CreateLaunchPlanRequestSchema>;
export type LaunchPlanMergePatchType = z.infer<typeof LaunchPlanMergePatchSchema>;
export type LaunchPlanStepMergePatchType = z.infer<typeof LaunchPlanStepMergePatchSchema>;
export type PaginatedLaunchPlansType = z.infer<typeof PaginatedLaunchPlansSchema>;
export type LaunchPlanStepsListType = z.infer<typeof LaunchPlanStepsListSchema>;
export type GetLaunchPlansQueryType = z.infer<typeof GetLaunchPlansQuerySchema>;
export type NotifyLaunchPlanAssignmentsRequestType = z.infer<typeof NotifyLaunchPlanAssignmentsRequestSchema>;
export type RecomputeLaunchPlansRequestType = z.infer<typeof RecomputeLaunchPlansRequestSchema>;
export type RecomputeLaunchPlansResultType = z.infer<typeof RecomputeLaunchPlansResultSchema>;
